/**
 * #2088, the Analyst panel: the same conversation as /analyst, on every page.
 *
 * Desktop: a rail on the right edge that folds out to a resizable column, so a
 * plant manager can ask about the dashboard they are looking at without leaving
 * it. Phone: a bottom sheet over the page, since a 24rem column has nowhere to
 * go at 390px.
 *
 * State comes from AnalystProvider (one conversation, two mounts). The panel
 * renders a compact transcript: charts and pinning stay on /analyst, one click
 * away through the header, rather than being reimplemented at half the width.
 */
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Sparkles,
  Send,
  Square,
  Plus,
  X,
  PanelRightOpen,
  ExternalLink,
  Loader2,
  BarChart3,
  AlertCircle,
} from "lucide-react";
import { useAllowedPages, useCanAccess } from "@/hooks/use-rbac";
import { isPathAllowed } from "@shared/rbac";
import { useGadgetPins, usePinsQuery } from "@/lib/gadget-pins";
import { MarkdownLink } from "@/lib/markdown-links";
import { useArtifacts } from "@/hooks/use-artifacts";
import { Skeleton } from "@/components/ui/Skeleton";
import { MIN_PANEL_WIDTH, MAX_PANEL_WIDTH } from "@/lib/analyst-panel-prefs";
import { useAnalyst, useAnalystPanel } from "./AnalystProvider";

/**
 * Cmd/Ctrl+/ toggles the panel. Not Cmd+J: that is the Downloads window in
 * Chrome, Edge and Firefox, and not Cmd+K, which is the command palette.
 */
const PANEL_SHORTCUT_KEY = "/";

/** The ask box is ours, so the shortcut works from it. Any other field is the
 *  user typing, and a chord that steals focus mid-sentence is a bug. */
function typingElsewhere(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") return false;
  if (el.closest("[data-analyst-ask]")) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}

/** Focusable children, same selector the app dialog uses for its trap. */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Pinned gadgets as links, not live frames: six sandboxed iframes behind a panel
 *  on every route is the cost the Phase 0 note warned about. */
function PinnedStrip() {
  const canGadgets = useCanAccess("/artifacts");
  const pinsQuery = usePinsQuery({ enabled: canGadgets });
  const { pins } = useGadgetPins({ enabled: canGadgets });
  const artifactsQuery = useArtifacts({ enabled: canGadgets });
  if (!canGadgets) return null;

  // Both queries are still out. Holding the row's height keeps the strip from
  // appearing under the reader mid-sentence and shoving the transcript down.
  if (pinsQuery.isPending || artifactsQuery.isPending) {
    return (
      <div className="flex gap-2 border-b border-border px-3 py-2" aria-hidden="true">
        <Skeleton className="h-[30px] w-28" />
        <Skeleton className="h-[30px] w-28" />
      </div>
    );
  }

  // #2272 class: a failed fetch is not "nothing pinned". Only a successful
  // answer earns the right to render the strip, or to render nothing.
  if (!pinsQuery.isSuccess || !artifactsQuery.isSuccess) return null;

  const byId = new Map((artifactsQuery.data ?? []).map((a) => [a.id, a]));
  const pinned = pins.map((id) => byId.get(id)).filter((a): a is NonNullable<typeof a> => !!a);
  if (pinned.length === 0) return null;
  return (
    <div className="flex gap-2 overflow-x-auto border-b border-border px-3 py-2">
      {pinned.map((a) => (
        <RouterLink
          key={a.id}
          to={`/artifacts/${a.id}`}
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <BarChart3 className="h-3.5 w-3.5 shrink-0" />
          <span className="max-w-32 truncate">{a.title}</span>
        </RouterLink>
      ))}
    </div>
  );
}

const markdownComponents = { a: MarkdownLink };

function Transcript() {
  const { messages, isLoading, streamingMessage } = useAnalyst();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: streamingMessage ? "auto" : "smooth" });
  }, [messages, streamingMessage]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
        Ask about the page you are on.
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
      {messages.map((msg) =>
        msg.role === "user" ? (
          <div key={msg.id} className="flex justify-end">
            <p className="max-w-[92%] whitespace-pre-wrap rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
              {msg.content}
            </p>
          </div>
        ) : (
          <div key={msg.id} className="rounded-lg border border-border bg-card px-3 py-2">
            {msg.tool_calls && msg.tool_calls.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1">
                {msg.tool_calls.map((t, i) => (
                  <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {t.tool}
                  </span>
                ))}
              </div>
            )}
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm [&_p]:my-1.5 [&_table]:block [&_table]:overflow-x-auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
            </div>
            {msg.charts && msg.charts.length > 0 && (
              <RouterLink
                to="/analyst"
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                See the chart in Data Analyst
              </RouterLink>
            )}
          </div>
        ),
      )}

      {streamingMessage && (
        <div className="rounded-lg border border-border bg-card px-3 py-2">
          {streamingMessage.liveTools.length > 0 && (
            <div className="mb-1.5 flex flex-wrap gap-1">
              {streamingMessage.liveTools.map((t, i) => (
                <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  {t.tool}
                </span>
              ))}
            </div>
          )}
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm [&_p]:my-1.5">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{streamingMessage.content}</ReactMarkdown>
          </div>
        </div>
      )}

      {isLoading && !streamingMessage?.content && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Analysing
        </p>
      )}
      <div ref={endRef} />
    </div>
  );
}

function AskBox({ onSent }: { onSent?: () => void }) {
  const { sendMessage, stopResponse, isLoading } = useAnalyst();
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const send = () => {
    if (!value.trim() || isLoading) return;
    const text = value;
    setValue("");
    void sendMessage(text);
    onSent?.();
  };

  return (
    <div
      data-analyst-ask
      className="flex items-end gap-2 border-t border-border p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
    >
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        rows={1}
        placeholder="Ask the Analyst"
        aria-label="Ask the Analyst"
        className="max-h-32 min-h-11 flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground outline-none focus:ring-2 focus:ring-ring sm:text-sm"
      />
      {isLoading ? (
        <button
          type="button"
          onClick={stopResponse}
          aria-label="Stop"
          title="Stop"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <Square className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={send}
          disabled={!value.trim()}
          aria-label="Send"
          title="Send"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground transition disabled:opacity-40 hover:brightness-110"
        >
          <Send className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function AnalystPanel() {
  const { open, width, openPanel, closePanel, setWidth } = useAnalystPanel();
  const { messages, clearChat, error, presentationMode: conversationIsPresenting } = useAnalyst();
  const { pathname, search } = useLocation();
  const pages = useAllowedPages();
  const sheetRef = useRef<HTMLElement>(null);
  // Duck-typed rather than instanceof HTMLElement: that global does not exist
  // everywhere this component is rendered.
  const returnFocus = useRef<{ focus: () => void; isConnected?: boolean } | null>(null);
  // Desktop from lg up, where the panel is a column beside the page rather than
  // a sheet over it. Read live: a rotate or a resize changes the answer.
  const [isDesktop, setIsDesktop] = useState(true);

  const presentationMode = new URLSearchParams(search).get("present") === "1";
  const available =
    pages !== null &&
    isPathAllowed("/analyst", pages) &&
    pathname !== "/analyst" &&
    !presentationMode &&
    // A conversation in presentation mode is being projected, and the panel is
    // the wrong surface for it.
    !conversationIsPresenting;

  // Which side of lg we are on, kept current through rotation and resize.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    setIsDesktop(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!available || typeof document === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === PANEL_SHORTCUT_KEY) {
        if (typingElsewhere(e.target)) return;
        e.preventDefault();
        // A page-level "/" handler (the Schedule search) listens on window and
        // does not check modifiers, so stop this chord reaching it.
        e.stopPropagation();
        if (open) closePanel();
        else openPanel();
      } else if (e.key === "Escape" && open) {
        closePanel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [available, open, openPanel, closePanel]);

  // The sheet covers the page below lg, so the page behind it must not scroll.
  // Rotating to a tablet width releases the lock rather than stranding it.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!(available && open && !isDesktop)) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [available, open, isDesktop]);

  // Opening remembers where focus was; closing puts it back. If that element
  // is gone by the time the panel closes, there is nothing to return focus
  // to, so leave it where the close action put it.
  useEffect(() => {
    if (!available) return;
    if (open) {
      if (!returnFocus.current && typeof document !== "undefined") {
        const active = document.activeElement as unknown as { focus?: () => void; isConnected?: boolean } | null;
        returnFocus.current = active && typeof active.focus === "function" ? (active as { focus: () => void; isConnected?: boolean }) : null;
      }
      return;
    }
    const previous = returnFocus.current;
    if (!previous) return;
    returnFocus.current = null;
    if (previous.isConnected !== false) previous.focus();
  }, [available, open]);

  // The sheet is modal over the page, so Tab stays inside it. The column on
  // desktop sits beside the page and must not trap anything.
  const trapTab = (e: React.KeyboardEvent<HTMLElement>) => {
    if (isDesktop || e.key !== "Tab") return;
    const scope = sheetRef.current;
    if (!scope) return;
    const focusable = [...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true",
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = typeof document === "undefined" ? null : document.activeElement;
    if (e.shiftKey && (active === first || !scope.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!available) return null;

  const title = messages.find((m) => m.role === "user")?.content.slice(0, 60) || "New conversation";

  // The Ask Crosbot pill (AskCrosbotButton) is the only opener now; when the
  // panel is closed there is nothing for this component to render.
  if (!open) return null;

  const startResize = (startX: number, startWidth: number) => {
    // Without this the drag selects the dashboard text it passes over.
    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const onMove = (e: PointerEvent) => setWidth(startWidth + (startX - e.clientX));
    const onUp = () => {
      document.body.style.userSelect = previousSelect;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <>
      <div
        onClick={closePanel}
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/40 lg:hidden print:hidden"
      />
      <aside
        ref={sheetRef}
        onKeyDown={trapTab}
        role="complementary"
        aria-label="Analyst"
        style={{ ["--analyst-w" as string]: `${width}px` }}
        className="fixed inset-x-0 bottom-0 z-50 flex h-[85dvh] flex-col rounded-t-2xl border-t border-border bg-card shadow-lg lg:static lg:z-auto lg:h-auto lg:w-[var(--analyst-w)] lg:shrink-0 lg:rounded-none lg:border-l lg:border-t-0 lg:shadow-none print:hidden"
      >
        <div
          role="separator"
          aria-label="Resize the Analyst panel"
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={MIN_PANEL_WIDTH}
          aria-valuemax={MAX_PANEL_WIDTH}
          tabIndex={0}
          onPointerDown={(e) => startResize(e.clientX, width)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setWidth(width + 32);
            if (e.key === "ArrowRight") setWidth(width - 32);
          }}
          className="absolute inset-y-0 left-0 hidden w-1.5 cursor-col-resize hover:bg-primary/30 focus-visible:bg-primary/40 lg:block"
        />

        <div className="mx-auto mt-2 h-1 w-9 rounded-full bg-border lg:hidden" aria-hidden="true" />

        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</p>
          <RouterLink
            to="/analyst"
            title="Open in Data Analyst"
            aria-label="Open in Data Analyst"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4" />
          </RouterLink>
          <button
            type="button"
            onClick={clearChat}
            title="Start a new conversation"
            aria-label="Start a new conversation"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={closePanel}
            title="Close the Analyst panel"
            aria-label="Close the Analyst panel"
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4 lg:hidden" />
            <PanelRightOpen className="hidden h-4 w-4 lg:block" />
          </button>
        </div>

        <PinnedStrip />
        <Transcript />

        {error && (
          <p className="flex items-start gap-2 border-t border-border px-3 py-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}

        <AskBox />
      </aside>
    </>
  );
}
